/**
 * Unmatched Invoices List Component
 * 
 * Displays Xero invoices that couldn't be auto-matched to shipments.
 * Allows manual matching and re-running auto-match.
 */

import React, { useState } from 'react';
import {
  Table,
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Space,
  Button,
  Typography,
  Tooltip,
  Empty,
  Modal,
  Input,
  AutoComplete,
  Spin,
  message,
  Alert,
  Descriptions,
  Progress,
} from 'antd';
import {
  SearchOutlined,
  LinkOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  UserOutlined,
  DollarOutlined,
  CalendarOutlined,
  SplitCellsOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { xeroApi } from '../../services/api';
import api from '../../services/api';
import InvoiceAllocationModal from './InvoiceAllocationModal';

const { Title, Text, Paragraph } = Typography;

// Color constants
const colors = {
  success: '#169959',
  error: '#C32928',
  warning: '#E6B339',
  info: '#179DB5',
  textSecondary: '#8C8C8C',
};

interface SuggestedMatch {
  shipmentId: string;
  reference: string;
  contractReference: string;
  confidence: number;
}

interface UnmatchedInvoice {
  invoiceId: string;
  invoiceNumber: string;
  contact: string;
  date: string;
  dueDate: string;
  total: number;
  amountDue: number;
  reference?: string;
  suggestedMatches?: SuggestedMatch[];
}

interface ShipmentSearchResult {
  id: string;
  reference: string;
  contract?: {
    reference: string;
    buyer?: { name: string; shortName: string };
    supplier?: { name: string; shortName: string };
  };
}

const UnmatchedInvoicesList: React.FC = () => {
  const queryClient = useQueryClient();
  const [matchModalVisible, setMatchModalVisible] = useState(false);
  const [allocationModalVisible, setAllocationModalVisible] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<UnmatchedInvoice | null>(null);
  const [shipmentSearch, setShipmentSearch] = useState('');
  const [selectedShipment, setSelectedShipment] = useState<ShipmentSearchResult | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  // Fetch unmatched invoices
  const {
    data: invoicesData,
    isLoading,
    refetch,
    error,
  } = useQuery({
    queryKey: ['unmatched-invoices'],
    queryFn: async () => {
      const response = await xeroApi.getUnmatchedInvoices();
      return response as unknown as { invoices: UnmatchedInvoice[] };
    },
    staleTime: 60000, // 1 minute
  });

  // Search shipments for manual matching
  const { data: shipmentResults, isLoading: searchingShipments } = useQuery({
    queryKey: ['shipment-search-match', shipmentSearch],
    queryFn: async () => {
      if (!shipmentSearch || shipmentSearch.length < 2) return [];
      // Strip shipment suffix (.01, .02, etc.) from contract refs for search
      // e.g., "CR-BR25128.01" -> "CR-BR25128"
      const searchTerm = shipmentSearch.replace(/(\.\d{2})$/, '');
      // Use /contracts endpoint with search param and include=shipments
      const response = await api.get('/contracts', {
        params: { search: searchTerm, limit: 10, include: 'shipments' },
      });
      // Extract shipments from contracts
      const shipments: ShipmentSearchResult[] = [];
      for (const contract of (response as any).contracts || []) {
        for (const shipment of contract.shipments || []) {
          shipments.push({
            id: shipment.id,
            reference: shipment.reference || `${contract.reference}-${shipment.id.slice(-4)}`,
            contract: {
              reference: contract.reference,
              buyer: contract.buyer,
              supplier: contract.supplier,
            },
          });
        }
      }
      return shipments;
    },
    enabled: shipmentSearch.length >= 2,
  });

  // Match invoice mutation
  const matchInvoiceMutation = useMutation({
    mutationFn: (data: { invoiceId: string; shipmentId: string }) =>
      xeroApi.matchInvoice(data.invoiceId, data.shipmentId),
    onSuccess: () => {
      message.success('Invoice matched successfully');
      queryClient.invalidateQueries({ queryKey: ['unmatched-invoices'] });
      setMatchModalVisible(false);
      setSelectedInvoice(null);
      setSelectedShipment(null);
      setShipmentSearch('');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to match invoice');
    },
  });

  const invoices = invoicesData?.invoices || [];

  // Ignore invoice mutation
  const ignoreInvoiceMutation = useMutation({
    mutationFn: (data: { invoiceId: string; reason?: string }) =>
      xeroApi.ignoreInvoice(data.invoiceId, data.reason),
    onSuccess: () => {
      message.success('Invoice marked as ignored');
      queryClient.invalidateQueries({ queryKey: ['unmatched-invoices'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to ignore invoice');
    },
  });

  // Open match modal (simple 1:1 match)
  const handleOpenMatchModal = (invoice: UnmatchedInvoice) => {
    setSelectedInvoice(invoice);
    setMatchModalVisible(true);
  };

  // Open allocation modal (multi-shipment allocation)
  const handleOpenAllocationModal = (invoice: UnmatchedInvoice) => {
    setSelectedInvoice(invoice);
    setAllocationModalVisible(true);
  };

  // Handle ignore invoice
  const handleIgnoreInvoice = (invoice: UnmatchedInvoice) => {
    Modal.confirm({
      title: 'Ignore Invoice',
      content: (
        <div>
          <p>Mark invoice <strong>{invoice.invoiceNumber}</strong> as ignored?</p>
          <p style={{ color: colors.textSecondary, fontSize: 12 }}>
            Use this for invoices that don't need shipment matching (samples, fees, etc.)
          </p>
        </div>
      ),
      okText: 'Ignore',
      okButtonProps: { danger: true },
      onOk: () => {
        ignoreInvoiceMutation.mutate({ invoiceId: invoice.invoiceId, reason: 'Not applicable' });
      },
    });
  };

  // Match with suggested shipment
  const handleSuggestedMatch = (invoice: UnmatchedInvoice, shipmentId: string) => {
    matchInvoiceMutation.mutate({
      invoiceId: invoice.invoiceId,
      shipmentId,
    });
  };

  // Handle manual shipment selection
  const handleShipmentSelect = (shipment: ShipmentSearchResult) => {
    setSelectedShipment(shipment);
    setShipmentSearch(shipment.reference);
  };

  // Submit manual match
  const handleSubmitMatch = () => {
    if (!selectedInvoice || !selectedShipment) return;
    matchInvoiceMutation.mutate({
      invoiceId: selectedInvoice.invoiceId,
      shipmentId: selectedShipment.id,
    });
  };

  // Table columns
  const columns: ColumnsType<UnmatchedInvoice> = [
    {
      title: 'Invoice',
      key: 'invoice',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.invoiceNumber}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.reference || 'No reference'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Contact',
      dataIndex: 'contact',
      key: 'contact',
      width: 200,
      render: (contact) => (
        <Space>
          <UserOutlined style={{ color: colors.textSecondary }} />
          <Text>{contact}</Text>
        </Space>
      ),
      filterSearch: true,
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 140,
      align: 'right',
      render: (_, record) => (
        <Space direction="vertical" size={0} style={{ width: '100%', textAlign: 'right' }}>
          <Text strong>${record.total.toFixed(2)}</Text>
          {record.amountDue !== record.total && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Due: ${record.amountDue.toFixed(2)}
            </Text>
          )}
        </Space>
      ),
      sorter: (a, b) => a.total - b.total,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => dayjs(date).format('MMM D, YYYY'),
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 120,
      render: (dueDate) => {
        const isPastDue = dayjs(dueDate).isBefore(dayjs(), 'day');
        return (
          <Text style={{ color: isPastDue ? colors.error : undefined }}>
            {dayjs(dueDate).format('MMM D, YYYY')}
            {isPastDue && (
              <ExclamationCircleOutlined style={{ marginLeft: 4, color: colors.error }} />
            )}
          </Text>
        );
      },
    },
    {
      title: 'Suggested Matches',
      key: 'suggestedMatches',
      width: 200,
      render: (_, record) => {
        if (!record.suggestedMatches || record.suggestedMatches.length === 0) {
          return <Text type="secondary">No suggestions</Text>;
        }
        return (
          <Space direction="vertical" size={2}>
            {record.suggestedMatches.slice(0, 2).map((match) => (
              <Tooltip key={match.shipmentId} title={`Confidence: ${(match.confidence * 100).toFixed(0)}%`}>
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: 'auto' }}
                  onClick={() => handleSuggestedMatch(record, match.shipmentId)}
                >
                  <Space size={4}>
                    <Progress
                      type="circle"
                      percent={match.confidence * 100}
                      size={18}
                      showInfo={false}
                      strokeColor={match.confidence > 0.7 ? colors.success : colors.warning}
                    />
                    <span>{match.reference}</span>
                  </Space>
                </Button>
              </Tooltip>
            ))}
            {record.suggestedMatches.length > 2 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                +{record.suggestedMatches.length - 2} more
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 240,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Match to single shipment">
            <Button
              size="small"
              icon={<LinkOutlined />}
              onClick={() => handleOpenMatchModal(record)}
            >
              Match
            </Button>
          </Tooltip>
          <Tooltip title="Allocate to multiple shipments">
            <Button
              type="primary"
              size="small"
              icon={<SplitCellsOutlined />}
              onClick={() => handleOpenAllocationModal(record)}
            >
              Allocate
            </Button>
          </Tooltip>
          <Tooltip title="Ignore (samples, fees, etc.)">
            <Button
              size="small"
              icon={<StopOutlined />}
              onClick={() => handleIgnoreInvoice(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Summary statistics
  const totalAmount = invoices.reduce((sum, inv) => sum + inv.total, 0);
  const totalAmountDue = invoices.reduce((sum, inv) => sum + inv.amountDue, 0);
  const pastDueCount = invoices.filter((inv) => dayjs(inv.dueDate).isBefore(dayjs(), 'day')).length;

  return (
    <div>
      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Unmatched Invoices"
              value={invoices.length}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: colors.warning }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Total Amount"
              value={totalAmount}
              precision={2}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Amount Due"
              value={totalAmountDue}
              precision={2}
              prefix="$"
              valueStyle={{ color: totalAmountDue > 0 ? colors.info : undefined }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Past Due"
              value={pastDueCount}
              valueStyle={{ color: pastDueCount > 0 ? colors.error : colors.success }}
            />
          </Card>
        </Col>
      </Row>

      {/* Error Alert */}
      {error && (
        <Alert
          message="Failed to load invoices"
          description="Unable to fetch unmatched invoices from Xero. Please check your connection."
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          action={
            <Button size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      )}

      {/* Action Bar */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Space>
          <Text type="secondary">
            {invoices.length > 0
              ? `Showing ${invoices.length} unmatched invoices`
              : 'All invoices are matched'}
          </Text>
        </Space>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
            Refresh
          </Button>
        </Space>
      </div>

      {/* Table */}
      <Table
        dataSource={invoices}
        columns={columns}
        rowKey="invoiceId"
        loading={isLoading}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `${total} invoices`,
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space direction="vertical">
                  <CheckCircleOutlined style={{ fontSize: 32, color: colors.success }} />
                  <Text>All invoices are matched!</Text>
                  <Text type="secondary">No unmatched invoices found in Xero.</Text>
                </Space>
              }
            />
          ),
        }}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as string[]),
        }}
      />

      {/* Manual Match Modal */}
      <Modal
        title={
          <Space>
            <LinkOutlined />
            Match Invoice to Shipment
          </Space>
        }
        open={matchModalVisible}
        onCancel={() => {
          setMatchModalVisible(false);
          setSelectedInvoice(null);
          setSelectedShipment(null);
          setShipmentSearch('');
        }}
        onOk={handleSubmitMatch}
        okText="Match Invoice"
        okButtonProps={{ disabled: !selectedShipment, loading: matchInvoiceMutation.isPending }}
        width={600}
      >
        {selectedInvoice && (
          <>
            {/* Invoice Details */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Invoice">{selectedInvoice.invoiceNumber}</Descriptions.Item>
                <Descriptions.Item label="Contact">{selectedInvoice.contact}</Descriptions.Item>
                <Descriptions.Item label="Amount">${selectedInvoice.total.toFixed(2)}</Descriptions.Item>
                <Descriptions.Item label="Due">${selectedInvoice.amountDue.toFixed(2)}</Descriptions.Item>
                <Descriptions.Item label="Date">{dayjs(selectedInvoice.date).format('MMM D, YYYY')}</Descriptions.Item>
                <Descriptions.Item label="Reference">{selectedInvoice.reference || '-'}</Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Suggested Matches */}
            {selectedInvoice.suggestedMatches && selectedInvoice.suggestedMatches.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>Suggested Matches:</Text>
                <div style={{ marginTop: 8 }}>
                  {selectedInvoice.suggestedMatches.map((match) => (
                    <Button
                      key={match.shipmentId}
                      style={{ marginRight: 8, marginBottom: 8 }}
                      onClick={() => {
                        setSelectedShipment({
                          id: match.shipmentId,
                          reference: match.reference,
                          contract: { reference: match.contractReference },
                        });
                        setShipmentSearch(match.reference);
                      }}
                    >
                      <Space>
                        <Progress
                          type="circle"
                          percent={match.confidence * 100}
                          size={16}
                          showInfo={false}
                          strokeColor={match.confidence > 0.7 ? colors.success : colors.warning}
                        />
                        {match.reference}
                        <Text type="secondary">({match.contractReference})</Text>
                      </Space>
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Shipment Search */}
            <div>
              <Text strong>Search Shipment:</Text>
              <AutoComplete
                value={shipmentSearch}
                onChange={setShipmentSearch}
                options={(shipmentResults || []).map((s) => ({
                  value: s.reference,
                  label: (
                    <div>
                      <Text strong>{s.reference}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {s.contract?.reference} | {s.contract?.buyer?.shortName || s.contract?.buyer?.name} / {s.contract?.supplier?.shortName || s.contract?.supplier?.name}
                      </Text>
                    </div>
                  ),
                  shipment: s,
                }))}
                onSelect={(_, option: any) => handleShipmentSelect(option.shipment)}
                placeholder="Search by shipment reference, contract, or party..."
                style={{ width: '100%', marginTop: 8 }}
                notFoundContent={
                  searchingShipments ? <Spin size="small" /> : 
                  shipmentSearch.length < 2 ? 'Type at least 2 characters' : 'No shipments found'
                }
              >
                <Input prefix={<SearchOutlined />} />
              </AutoComplete>
            </div>

            {/* Selected Shipment */}
            {selectedShipment && (
              <Card size="small" style={{ marginTop: 16, backgroundColor: '#f6ffed' }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div>
                    <CheckCircleOutlined style={{ color: colors.success, marginRight: 8 }} />
                    <Text strong>Selected: {selectedShipment.reference}</Text>
                  </div>
                  <Text type="secondary">
                    Contract: {selectedShipment.contract?.reference}
                    {selectedShipment.contract?.buyer && ` | Buyer: ${selectedShipment.contract.buyer.shortName || selectedShipment.contract.buyer.name}`}
                    {selectedShipment.contract?.supplier && ` | Supplier: ${selectedShipment.contract.supplier.shortName || selectedShipment.contract.supplier.name}`}
                  </Text>
                </Space>
              </Card>
            )}
          </>
        )}
      </Modal>

      {/* Invoice Allocation Modal */}
      <InvoiceAllocationModal
        open={allocationModalVisible}
        invoice={selectedInvoice}
        onClose={() => {
          setAllocationModalVisible(false);
          setSelectedInvoice(null);
        }}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['unmatched-invoices'] });
        }}
      />
    </div>
  );
};

export default UnmatchedInvoicesList;
